import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting)),
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

const STATS = [
  { value: '150+', label: 'Clients Served' },
  { value: '9', label: 'Industry Sectors' },
  { value: '10+', label: 'Years of Experience' },
  // { value: '20+', label: 'Insurance Carriers' },
];

const CLIENTS = [
  { src: '/logo/citraborneo.png', alt: 'Citra Borneo Indah Group', sector: 'Energy & Resources' },
  { src: '/logo/colorobbia.png', alt: 'Colorobbia', sector: 'Manufacturing' },
  { src: '/logo/indomaret.png', alt: 'Indomaret', sector: 'Retail' },
  { src: '/logo/kencana.png', alt: 'Kencana Jaya Transindo', sector: 'Travel' },
  { src: '/logo/lifepack.png', alt: 'Lifepack', sector: 'Healthcare' },
  { src: '/logo/makamotors.png', alt: 'Maka Motors', sector: 'Automotive' },
  { src: '/logo/mganiklogo.png', alt: 'mGanik', sector: 'Agriculture' },
  { src: '/logo/payfazz.png', alt: 'Payfazz', sector: 'Financial Technology' },
  { src: '/logo/pelabuhantigabersaudara.png', alt: 'Pelabuhan Tiga Bersaudara', sector: 'Logistics & Maritime' },
  { src: '/logo/pluangg.png', alt: 'Pluang', sector: 'Financial Technology' },
  { src: '/logo/tiket.png', alt: 'Tiket', sector: 'Travel & Technology' },
  { src: '/logo/tiptiplogo.png', alt: 'Tiptip', sector: 'Ticketing & Technology' },
  { src: '/logo/tokocrypto.png', alt: 'Tokocrypto', sector: 'Financial Technology' },
  { src: '/logo/xendit.png', alt: 'Xendit', sector: 'Financial Technology' },
];

// const PARTNERS = [
//   { src: '/logo/axa.png', alt: 'AXA' },
//   { src: '/logo/aca.png', alt: 'ACA' },
//   { src: '/logo/bupa.png', alt: 'Bupa' },
//   { src: '/logo/aia.png', alt: 'AIA' },
//   { src: '/logo/ramayana.png', alt: 'Ramayana' },
//   { src: '/logo/greateastern.png', alt: 'Great Eastern' },
//   { src: '/logo/takaful.png', alt: 'Takaful' },
//   { src: '/logo/sunday.png', alt: 'Sunday' },
//   { src: '/logo/zurich.png', alt: 'Zurich', large: true }
// ];

const TESTIMONIALS = [
  {
    quote:
      "Redoura's team walked us through every clause of our policy until we actually understood what we were paying for. That kind of clarity is rare.",
    name: 'Client Name',
    role: 'Operations Director',
  },
  {
    quote:
      'When we had a claim, they handled the entire process on our behalf. We barely had to lift a finger.',
    name: 'Client Name',
    role: 'Finance Manager',
  },
];

function StatItem({ value, label }) {
  const [ref, visible] = useInView(0.5);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <p
        className="text-4xl sm:text-5xl text-white mb-2"
        style={{ fontFamily: "'Fraunces', 'Georgia', serif" }}
      >
        {value}
      </p>
      <p className="font-mono text-xs tracking-[0.2em] text-white/50 uppercase">
        {label}
      </p>
    </div>
  );
}

function ClientCard({ logo }) {
  return (
    <div className="group relative bg-white border border-transparent hover:bg-[#0e1e52] hover:border-[#ce381b]/50 rounded-xl h-32 sm:h-36 flex items-center justify-center px-6 transition-colors duration-300">
      {/* client name (top) */}
      <span className="absolute top-3 left-0 right-0 px-3 text-center font-mono text-[10px] leading-tight tracking-widest text-white/70 uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {logo.alt}
      </span>

      <img
        src={logo.src}
        alt={logo.alt}
        className="max-h-10 sm:max-h-12 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300"
      />

      {/* sector (bottom) */}
      <span className="absolute bottom-3 left-0 right-0 px-3 text-center font-mono text-[10px] leading-tight tracking-widest text-white/40 uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {logo.sector}
      </span>
    </div>
  );
}

// function PartnerCard({ logo }) {
//   return (
//     <div className="group relative bg-white border border-transparent hover:bg-[#0e1e52] hover:border-[#ce381b]/50 rounded-xl h-32 sm:h-36 flex items-center justify-center px-6 transition-colors duration-300">
//       <img
//         src={logo.src}
//         alt={logo.alt}
//         className={`w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300 ${
//           logo.large ? 'max-h-20 sm:max-h-24' : 'max-h-10 sm:max-h-12'
//         }`}
//       />
//     </div>
//   );
// }

function TestimonialCard({ quote, name, role }) {
  return (
    <div className="border-t border-white/10 pt-8">
      <p className="text-white/80 text-lg sm:text-xl leading-relaxed mb-6">
        “{quote}”
      </p>
      <p className="font-mono text-xs tracking-widest text-[#ce381b] uppercase">
        {name} <span className="text-white/30">— {role}</span>
      </p>
    </div>
  );
}

function OurClients() {
  return (
    <div className="bg-[#0e1e52]">
      <Navbar />

      {/* faint grid, consistent with Services hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 lg:pt-40">
          <p className="font-mono text-xs tracking-[0.25em] text-[#ce381b] uppercase mb-6">
            Our Clients
          </p>

          <h1
            className="text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mb-8"
            style={{ fontFamily: "'Fraunces', 'Georgia', serif" }}
          >
            Businesses across every sector trust us with what matters most.
          </h1>

          <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
            From retail and technology to energy and manufacturing, we've built
            long-term relationships with companies who need more than a policy —
            they need a partner who understands their risk.
          </p>
        </div>

        {/* stats row */}
        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-3 gap-10">
            {STATS.map((s) => (
              <StatItem key={s.label} value={s.value} label={s.label} />
            ))}
          </div>
        </div>
      </section>

      {/* logo grid */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <p className="font-mono text-xs tracking-[0.25em] text-[#ce381b] uppercase mb-10">
            Who We Work With
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {CLIENTS.map((logo) => (
              <ClientCard key={logo.alt} logo={logo} />
            ))}
          </div>
        </div>
      </section>

      {/* <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 lg:pb-28">
          <p className="font-mono text-xs tracking-[0.25em] text-[#ce381b] uppercase mb-10">
            Insurance Partnerships
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {PARTNERS.map((logo) => (
              <PartnerCard key={logo.alt} logo={logo} />
            ))}
          </div>
        </div>
      </section> */}

      {/* testimonials */}
      <section className="relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32">
          <p className="font-mono text-xs tracking-[0.25em] text-[#ce381b] uppercase mb-10">
            In Their Words
          </p>

          <div className="grid sm:grid-cols-2 gap-10 sm:gap-14">
            {TESTIMONIALS.map((t) => (
              <TestimonialCard key={t.name + t.role} {...t} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default OurClients;